import { motion } from "framer-motion";
import { ShieldCheck, MapPin, HeartHandshake, Eye, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";

const VALUES = [
  { icon: ShieldCheck, title: "Absolute Integrity", desc: "Your financial information is handled with strict confidentiality and honest, transparent advice — always." },
  { icon: MapPin, title: "Local Florida Roots", desc: "Based in Tampa, we understand Florida tax rules and the needs of our local community." },
  { icon: HeartHandshake, title: "Personalized Attention", desc: "You're never a file number. We build lasting relationships and tailor every engagement to your situation." },
  { icon: Eye, title: "Commitment to Accuracy", desc: "Careful review and precise preparation on every return, ledger, and report we deliver." },
];

export default function About({ onBookAppointment }) {
  return (
    <div data-testid="about-page" className="pt-16">
      <SEO path="/about-us"
        title="About Us | Marizema Tax Service — Tampa, FL"
        description="Marizema Tax Service LLC provides tax and bookkeeping services to individuals and businesses in Tampa, Florida, with a commitment to accuracy, integrity and personalized attention." />
      <section className="relative py-20 sm:py-28 overflow-hidden bg-[#0B2545]">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Financial advisor meeting a client" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/92 to-[#0B2545]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-3">About Us</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              About Marizema Tax Service LLC
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed">
              Marizema Tax Service LLC is dedicated to providing exceptional tax services to individuals and businesses in Tampa, Florida — and beyond. With a commitment to accuracy, integrity, and personalized attention, we build lasting relationships with our clients and help them achieve their financial goals.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-[#0B2545]">What we stand for</h2>
            <p className="mt-3 text-[#1F2933]/80">The values behind every return, every ledger, and every conversation.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.45 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C9A227]/50 hover:shadow-lg hover:shadow-slate-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-[#0B2545] flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-display text-lg font-semibold text-[#0B2545]">{title}</h3>
                <p className="mt-2 text-sm text-[#1F2933]/75 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#14375E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-white">Our mission</h2>
          <p className="mt-5 font-serif-display text-xl sm:text-2xl text-slate-200 italic leading-relaxed">
            "To empower individuals and business owners in Tampa with transparent, dependable, and high-precision financial services."
          </p>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-200">
            {["Individuals & families", "Small businesses & LLCs", "Freelancers & contractors", "Rental property owners"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-[#D4AF37]" /> {t}</li>
            ))}
          </ul>
          <button data-testid="about-cta-book-appointment" onClick={onBookAppointment}
            className="mt-10 px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
            Meet the Team — Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
}
